import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import {
  normalizeInstructorAiProfile,
  emptyInstructorAiProfile,
  isDerivedStale,
} from '../utils/instructorAiProfile';
import { computeLearnedSignals } from '../utils/instructorProfileCompute';

/**
 * Load and maintain the instructor AI profile from Firestore.
 *
 * - Reads instructorAiProfiles/{instructorId} once on mount.
 * - If the doc doesn't exist, writes an empty profile and uses it.
 * - If the derived layer is stale (>7 days), triggers a lazy recompute
 *   in the background without blocking the caller.
 *
 * @param {string|null} instructorId
 * @returns {{ profile: object|null, loading: boolean, error: string|null, updateOverrides: fn }}
 */
export function useInstructorAiProfile(instructorId) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!instructorId) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    async function load() {
      try {
        const ref = doc(db, 'instructorAiProfiles', instructorId);
        const snap = await getDoc(ref);

        if (cancelled) return;

        let normalized;
        if (!snap.exists()) {
          // Derive orgId from the doc if possible; fall back to null — caller can update later
          const empty = emptyInstructorAiProfile(instructorId, null);
          // Fire-and-forget: write the empty shell so future reads are faster
          setDoc(ref, { ...empty, lastUpdated: serverTimestamp() }).catch(() => {});
          normalized = empty;
        } else {
          normalized = normalizeInstructorAiProfile({ instructorId, ...snap.data() });
        }

        setProfile(normalized);

        // Trigger lazy derived recompute in background if stale
        if (isDerivedStale(normalized) && normalized.orgId) {
          refreshDerived(ref, instructorId, normalized.orgId);
        }
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Failed to load instructor profile');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [instructorId]);

  /**
   * Write explicit overrides (tone, verbosity, drillStyle, usesAnalogies, phrasesBank)
   * back to Firestore and update local state immediately.
   *
   * @param {object} overridesPatch — partial overrides object
   */
  async function updateOverrides(overridesPatch) {
    if (!instructorId || !profile) return;
    const ref = doc(db, 'instructorAiProfiles', instructorId);
    const patchedOverrides = { ...profile.overrides, ...overridesPatch };
    const update = { overrides: patchedOverrides, lastUpdated: serverTimestamp() };
    // Optimistic local update
    setProfile(prev => prev ? { ...prev, overrides: patchedOverrides } : prev);
    try {
      await updateDoc(ref, update);
    } catch (err) {
      // Revert on failure
      setProfile(prev => prev ? { ...prev, overrides: profile.overrides } : prev);
      throw err;
    }
  }

  return { profile, loading, error, updateOverrides };
}

/**
 * Background worker — recomputes derived signals and writes them back.
 * Never throws; errors are silently swallowed so they never block generation.
 */
async function refreshDerived(ref, instructorId, orgId) {
  try {
    const derived = await computeLearnedSignals(db, instructorId, orgId);
    if (!derived) return;
    await updateDoc(ref, {
      'derived.preferredDrills': derived.preferredDrills,
      'derived.lessonStructure': derived.lessonStructure,
      'derived.lastDerivedAt': serverTimestamp(),
    });
  } catch (_) {
    // intentionally silent
  }
}
